/* Verifica all'ingresso: impostazioni, «Ingresso libero», protezione dei booster, utenti in attesa e registro. */
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  AlertTriangle, BadgeCheck, CheckCircle2, Clock, DoorOpen, Gem, History, Info, Loader2, MousePointerClick, Send, ShieldCheck,
  ShieldPlus, Siren, UserCheck, UserX, Users, Calculator, Image as ImageIcon,
} from "lucide-react";
import { apiError, botDelete, botGet, botPost, botPut } from "@/lib/api";
import { useData } from "@/context/DataContext";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { DiscordEmbedPreview } from "@/components/DiscordEmbedPreview";
import {
  Field as KitField, FeatureGate, HowItWorks, PageHeader, PillTabs, SimpleSelect, TextArea, TextInput,
  ToggleRow as KitToggleRow, Empty, cleanMessage, formatDateTime, outlineBtn, primaryBtn, timeAgo, useFeature,
} from "@/components/kit";
import { ChannelPick, InfoLabel, InfoTip, RolePicker } from "@/pages/tickets/ticketsKit";
import CaptchaStyle from "./CaptchaStyle";

const api = {
  overview: () => botGet("verification/overview"),
  startFree: (minutes, autoVerify) => botPost("verification/free-entry", { minutes, autoVerify }),
  stopFree: () => botDelete("verification/free-entry"),
  verify: (id) => botPost(`verification/members/${id}/verify`, {}),
  protect: (id) => botPut(`verification/members/${id}/protected`, {}),
  unprotect: (id) => botDelete(`verification/members/${id}/protected`),
  publish: () => botPost("verification/publish", {}),
};

const Field = ({ info, label, ...rest }) => <KitField label={label && info ? <InfoLabel info={info}>{label}</InfoLabel> : label} {...rest} />;
const ToggleRow = ({ info, title, ...rest }) => <KitToggleRow title={info ? <InfoLabel info={info}>{title}</InfoLabel> : title} {...rest} />;

function Section({ title, desc, icon: Icon, info, children, right, tone = "#5865F2" }) {
  return (
    <div className="rounded-2xl border border-[#232838] bg-[#161923] p-5">
      <div className="mb-4 flex items-start gap-3">
        {Icon && <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl" style={{ background: `${tone}22` }}><Icon className="h-4.5 w-4.5" style={{ color: tone }} /></div>}
        <div className="min-w-0 flex-1"><p className="flex items-center gap-1.5 font-bold text-white">{title}<InfoTip text={info} /></p>{desc && <p className="text-xs text-[#949BA4]">{desc}</p>}</div>
        {right}
      </div>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

function ChoiceCard({ active, onClick, icon: Icon, title, desc, info }) {
  return (
    <div className="relative h-full">
      <button type="button" onClick={onClick}
        className={`flex h-full w-full flex-col items-start gap-2 rounded-2xl border p-4 text-left transition-all ${active ? "border-[#5865F2] bg-[#5865F2]/10 shadow-[0_0_0_1px_rgba(88,101,242,0.6)]" : "border-[#232838] bg-[#131620] hover:border-[#3a4160]"}`}>
        {active && <span className="absolute right-3 top-3 flex h-5 w-5 items-center justify-center rounded-full bg-[#5865F2]"><CheckCircle2 className="h-3.5 w-3.5 text-white" /></span>}
        <Icon className="h-5 w-5 text-[#8ea0ff]" />
        <span className="font-semibold text-white">{title}</span>
        <span className="pr-5 text-xs text-[#949BA4]">{desc}</span>
      </button>
      <InfoTip text={info} className="absolute bottom-3 right-3" />
    </div>
  );
}

/** Un solo ruolo, con nome e colore (riusa il selettore dei ticket). */
const OneRole = ({ value, onChange, placeholder }) => (
  <RolePicker value={value ? [value] : []} onChange={(ids) => onChange(ids[ids.length - 1] ?? null)} placeholder={value ? "Cambia ruolo" : placeholder} />
);

const useNow = (ms = 1000) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), ms); return () => clearInterval(t); }, [ms]);
  return now;
};
const left = (iso, now) => {
  const s = Math.max(0, Math.round((new Date(iso).getTime() - now) / 1000));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  return h ? `${h}h ${String(m).padStart(2, "0")}m` : `${m}:${String(sec).padStart(2, "0")}`;
};

// ─────────────────────────── ingresso libero ───────────────────────────
const DURATIONS = [30, 60, 120, 240, 480].map((m) => ({ value: m, label: m < 60 ? `${m} min` : `${m / 60} ${m === 60 ? "ora" : "ore"}` }));

function FreeEntryCard({ ov, setOv }) {
  const now = useNow();
  const [minutes, setMinutes] = useState(60);
  const [autoVerify, setAutoVerify] = useState(true);
  const [busy, setBusy] = useState(false);
  const fe = ov?.freeEntry;
  const active = fe && new Date(fe.until).getTime() > now;
  const run = async (fn, ok) => {
    setBusy(true);
    try { setOv(await fn()); if (ok) toast.success(ok); } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const extend = () => run(() => api.startFree(Math.min(1440, Math.ceil((new Date(fe.until).getTime() - Date.now()) / 60000) + 30), fe.autoVerify), "Aggiunti 30 minuti");

  if (active) {
    return (
      <div className="overflow-hidden rounded-2xl border border-[#23A55A]/50 bg-gradient-to-br from-[#23A55A]/20 via-[#23A55A]/[0.06] to-transparent p-5" data-testid="free-entry-active">
        <div className="flex flex-wrap items-center gap-4">
          <div className="relative flex h-12 w-12 items-center justify-center rounded-2xl bg-[#23A55A]/25">
            <DoorOpen className="h-6 w-6 text-[#57F287]" />
            <span className="absolute -right-1 -top-1 h-3 w-3 animate-ping rounded-full bg-[#57F287]" />
          </div>
          <div className="min-w-[200px] flex-1">
            <p className="flex items-center gap-1.5 text-lg font-extrabold text-white">Ingresso libero ATTIVO<InfoTip text="In questo momento chi entra nel server non viene fermato né dalla verifica né dall'anti-raid, e resta protetto anche dopo la fine (non verrà mai espulso per la verifica). Usalo quando fai entrare persone che devono mettere il boost." /></p>
            <p className="text-sm text-[#b8f0cc]">Finisce tra <b className="font-mono text-white">{left(fe.until, now)}</b> (alle {new Date(fe.until).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}) · entrati finora: <b className="text-white">{fe.joined ?? 0}</b>{fe.autoVerify ? " · ricevono subito il ruolo verificato" : ""}</p>
          </div>
          <div className="flex w-full gap-2 sm:w-auto">
            <Button variant="outline" disabled={busy} onClick={extend} className={outlineBtn}>+30 min</Button>
            <Button disabled={busy} onClick={() => run(api.stopFree, "Ingresso libero terminato")} className="bg-[#F23F43] font-semibold text-white hover:bg-[#d83236]" data-testid="free-entry-stop">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Termina ora
            </Button>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="rounded-2xl border border-[#232838] bg-[#161923] p-5" data-testid="free-entry-card">
      <div className="flex flex-wrap items-start gap-4">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#23A55A]/15"><DoorOpen className="h-6 w-6 text-[#57F287]" /></div>
        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-1.5 text-lg font-extrabold text-white">Ingresso libero<InfoTip text="Accendilo PRIMA di far entrare le persone che devono mettere il boost. Per il tempo che scegli, chi entra non viene fermato dalla verifica, non viene espulso e l'anti-raid non lo tocca (e non conta questi ingressi come un raid). Queste persone restano protette per sempre. Allo scadere tutto torna normale da solo." /></p>
          <p className="text-sm text-[#949BA4]">Per far entrare persone senza che la verifica o l'anti-raid le blocchino o le espellano. Si spegne da solo.</p>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <div className="flex flex-wrap gap-1.5">
          {DURATIONS.map((d) => (
            <button key={d.value} type="button" onClick={() => setMinutes(d.value)}
              className={`rounded-xl px-3 py-1.5 text-sm font-semibold transition-colors ${minutes === d.value ? "bg-[#23A55A] text-white" : "bg-[#131620] text-[#B5BAC1] hover:text-white"}`}>{d.label}</button>
          ))}
        </div>
        <label className="flex items-center gap-2 text-sm text-[#DBDEE1]">
          <Switch checked={autoVerify} onCheckedChange={setAutoVerify} />Dai subito il ruolo verificato
          <InfoTip text="Se attivo, chi entra durante l'ingresso libero riceve subito il ruolo dei verificati, senza premere nessun pulsante. Se spento entra solo protetto, ma deve comunque verificarsi per vedere i canali." />
        </label>
        <Button disabled={busy} onClick={() => run(() => api.startFree(minutes, autoVerify), "Ingresso libero attivato")} className="ml-auto bg-[#23A55A] font-semibold text-white hover:bg-[#1e8e4d]" data-testid="free-entry-start">
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <DoorOpen className="mr-2 h-4 w-4" />}Attiva ingresso libero
        </Button>
      </div>
    </div>
  );
}

// ─────────────────────────── riepilogo ───────────────────────────
function Stat({ icon: Icon, value, label, tone, info }) {
  return (
    <div className="flex items-center gap-3 rounded-2xl border border-[#232838] bg-[#161923] p-4">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl" style={{ background: `${tone}22`, color: tone }}><Icon className="h-5 w-5" /></div>
      <div className="min-w-0">
        <p className="text-2xl font-extrabold text-white">{value ?? "—"}</p>
        <p className="flex items-center gap-1 text-xs text-[#949BA4]">{label}<InfoTip text={info} /></p>
      </div>
    </div>
  );
}

function Health({ issues = [] }) {
  const list = issues.filter((i) => i.level !== "info");
  if (!list.length) return null;
  return (
    <div className="space-y-1.5 rounded-2xl border border-[#FEE75C]/30 bg-[#FEE75C]/[0.06] p-4">
      <p className="flex items-center gap-2 text-sm font-bold text-[#FEE75C]"><AlertTriangle className="h-4 w-4" />Da sistemare</p>
      {list.map((i, n) => <p key={n} className={`text-sm ${i.level === "error" ? "text-[#ffb3b4]" : "text-[#f5e6a0]"}`}>• {i.text}</p>)}
    </div>
  );
}

// ─────────────────────────── impostazioni ───────────────────────────
const KICK = [
  { value: 0, label: "Mai (consigliato all'inizio)" }, { value: 10, label: "10 minuti" }, { value: 30, label: "30 minuti" }, { value: 60, label: "1 ora" },
  { value: 180, label: "3 ore" }, { value: 360, label: "6 ore" }, { value: 720, label: "12 ore" }, { value: 1440, label: "24 ore" }, { value: 4320, label: "3 giorni" },
];

function Settings({ f, onPublished }) {
  const { channels } = useData();
  const [publishing, setPublishing] = useState(false);
  const prepare = (x) => ({ ...x, message: cleanMessage(x.message) });
  /** Un solo tasto: salva le modifiche, accende la verifica e pubblica (o aggiorna) il messaggio su Discord. */
  const publish = async () => {
    const v = f.value;
    const missing = [!v.roleId && "il ruolo da dare ai verificati", !v.channelId && "il canale di verifica"].filter(Boolean);
    if (missing.length) return toast.error(`Prima scegli ${missing.join(" e ")}`);
    setPublishing(true);
    try {
      if (f.dirty || !v.enabled) await botPut("features/verification", prepare({ ...structuredClone(v), enabled: true }));
      const res = await api.publish();
      await f.reload();
      onPublished();
      toast.success(v.messageId ? "Messaggio di verifica aggiornato su Discord" : "Verifica pubblicata su Discord", {
        description: "La verifica è attiva.", action: res?.url ? { label: "Apri", onClick: () => window.open(res.url, "_blank", "noopener") } : undefined,
      });
    } catch (e) { toast.error(apiError(e)); } finally { setPublishing(false); }
  };
  return (
    <FeatureGate feature={f}>
      {(v) => {
        const embed = v.message?.embeds?.[0] ?? { title: "", description: "", color: "#57F287" };
        const setEmbed = (p) => f.change({ message: { ...v.message, embeds: [{ ...embed, ...p }] } });
        const chName = channels.find((c) => c.id === v.channelId)?.name;
        return (
          <div className="space-y-5">
            <ToggleRow title={v.enabled ? "Verifica attiva" : "Verifica spenta"} desc="Se è spenta il pulsante di verifica non funziona e nessuno viene espulso." checked={v.enabled} onChange={(enabled) => f.change({ enabled })} testId="verify-enabled"
              info="L'interruttore generale. Spenta: il pulsante risponde «la verifica non è attiva» e il bot non espelle nessuno. L'ingresso libero e la protezione dei booster funzionano comunque." />

            <Section title="Ruoli" icon={BadgeCheck} info="Come funziona la verifica: i canali del server li vede solo chi ha il ruolo «verificato». Chi entra non ce l'ha, quindi vede solo il canale di verifica; preme il pulsante e riceve il ruolo.">
              <div className="grid gap-4 md:grid-cols-2">
                <Field label="Ruolo da dare ai verificati" info="Il ruolo che il bot dà a chi completa la verifica (es. «Membro» o «Verificato»). Deve stare SOTTO al ruolo del bot nelle impostazioni del server, altrimenti il bot non può assegnarlo.">
                  <OneRole value={v.roleId} onChange={(roleId) => f.change({ roleId })} placeholder="+ Scegli il ruolo" />
                </Field>
                <Field label="Ruolo «non verificato» (facoltativo)" info="Un ruolo che viene tolto quando la persona si verifica. Utile se vuoi un ruolo tipo «Nuovo arrivato». Se non ti serve lascia vuoto.">
                  <OneRole value={v.removeRoleId} onChange={(removeRoleId) => f.change({ removeRoleId })} placeholder="+ Nessuno" />
                </Field>
              </div>
              <ToggleRow title="Dai il ruolo «non verificato» a chi entra" desc={v.removeRoleId ? "Il bot lo assegna appena qualcuno entra, e lo toglie dopo la verifica." : "Scegli prima il ruolo qui sopra."}
                info="Se attivo, appena qualcuno entra il bot gli dà il ruolo «non verificato». Chi entra con l'ingresso libero non lo riceve." checked={v.giveRemoveRoleOnJoin && !!v.removeRoleId} onChange={(giveRemoveRoleOnJoin) => f.change({ giveRemoveRoleOnJoin })} />
            </Section>

            <Section title="Tipo di verifica" icon={ShieldCheck} info="Cosa deve fare la persona dopo aver premuto il pulsante.">
              <div className="grid gap-3 md:grid-cols-3">
                <ChoiceCard active={v.mode === "captcha"} onClick={() => f.change({ mode: "captcha" })} icon={ImageIcon} title="Captcha con immagine" desc="Deve ricopiare un codice scritto in un'immagine deformata. Consigliato."
                  info="Dopo il clic la persona vede (solo lei) un'immagine con un codice di 6 lettere e numeri, storto e disturbato, e lo ricopia in una finestrella. Blocca i bot. Se sbaglia riceve un codice nuovo; dopo 5 errori deve aspettare 2 minuti. C'è anche il tasto «Nuovo codice» se non si legge bene." />
                <ChoiceCard active={v.mode === "button"} onClick={() => f.change({ mode: "button" })} icon={MousePointerClick} title="Solo pulsante" desc="Un clic e sei dentro. Semplicissimo."
                  info="Basta premere il pulsante. È la più comoda, ma i bot più furbi possono superarla." />
                <ChoiceCard active={v.mode === "math"} onClick={() => f.change({ mode: "math" })} icon={Calculator} title="Piccola somma" desc="Deve risolvere un'operazione (es. 7 + 12)."
                  info="Dopo il clic si apre una finestra con una somma da risolvere, diversa ogni volta. Più semplice del captcha, ma meno sicura." />
              </div>
              {v.mode === "captcha" && <CaptchaStyle value={v.captcha} onChange={(captcha) => f.change({ captcha })} />}
            </Section>

            <Section title="Canale e messaggio" icon={Send} info="Il messaggio fisso con il pulsante di verifica, pubblicato nel canale che scegli. È l'unico canale che deve vedere chi non è ancora verificato.">
              <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(0,420px)]">
                <div className="space-y-4">
                  <Field label="Canale di verifica" info="Il canale dove compare il pulsante. Deve essere visibile a @everyone (anche ai non verificati). Se non ce l'hai, crealo dal menu.">
                    <ChannelPick value={v.channelId} onChange={(channelId) => f.change({ channelId })} types={["text", "announcement"]} placeholder="Es. #verifica" suggestName="verifica" testId="verify-channel" />
                  </Field>
                  <Field label="Titolo" info="Il titolo in grassetto del messaggio."><TextInput value={embed.title} onChange={(title) => setEmbed({ title })} maxLength={256} placeholder="✅ Verifica" /></Field>
                  <Field label="Testo" info="Spiega cosa fare, es. «Premi il pulsante qui sotto per accedere al server»."><TextArea value={embed.description} onChange={(description) => setEmbed({ description })} rows={3} maxLength={4000} /></Field>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <Field label="Testo del pulsante" info="La scritta sul pulsante verde."><TextInput value={v.buttonLabel} onChange={(buttonLabel) => f.change({ buttonLabel })} maxLength={80} /></Field>
                    <Field label="Colore" info="Il colore della barra laterale del messaggio.">
                      <div className="flex items-center gap-2">
                        <input type="color" value={/^#[0-9a-fA-F]{6}$/.test(embed.color || "") ? embed.color : "#57F287"} onChange={(e) => setEmbed({ color: e.target.value })} className="h-9 w-12 cursor-pointer rounded-md border border-[#232838] bg-transparent" />
                        <span className="font-mono text-xs text-[#949BA4]">{embed.color}</span>
                      </div>
                    </Field>
                  </div>
                  <Field label="Messaggio dopo la verifica" info="Cosa vede la persona (solo lei) quando ha finito la verifica."><TextInput value={v.successMessage} onChange={(successMessage) => f.change({ successMessage })} maxLength={500} /></Field>
                </div>
                <div className="space-y-3">
                  <DiscordEmbedPreview channelName={chName} fillPlaceholders={false} message={{ embeds: [embed], buttons: [{ label: v.buttonLabel, emoji: "✅", style: "success", action: "custom" }] }} />
                  <Button onClick={publish} disabled={publishing} className={`w-full ${primaryBtn}`} data-testid="verify-publish">
                    {publishing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}{v.messageId ? "Salva e aggiorna su Discord" : "Salva e pubblica su Discord"}
                  </Button>
                  <p className="text-center text-[11px] text-[#949BA4]">{v.messageId ? "Già pubblicato: aggiorna lo stesso messaggio, senza doppioni." : "Salva, accende la verifica e pubblica il pulsante nel canale."}</p>
                </div>
              </div>
            </Section>

            <Section title="Protezioni e booster" icon={Gem} tone="#F47FFF" info="Qui decidi chi non deve MAI essere fermato o espulso. I booster sono protetti in automatico.">
              <ToggleRow title="I booster sono sempre al sicuro" desc="Chi mette il boost viene verificato da solo e non viene mai espulso (né dalla verifica né dall'anti-raid)."
                info="Appena qualcuno mette il boost il bot: gli dà il ruolo verificato, gli toglie il ruolo «non verificato», gli toglie un eventuale timeout dell'anti-raid e lo segna come protetto per sempre. Consigliato: lascialo acceso." checked={v.boostersBypass !== false} onChange={(boostersBypass) => f.change({ boostersBypass })} testId="verify-boosters" />
              <Field label="Ruoli sempre protetti" info="Chi ha uno di questi ruoli non viene mai espulso dalla verifica e l'anti-raid non lo tocca. Gli amministratori del server sono già protetti.">
                <RolePicker value={v.exemptRoleIds ?? []} onChange={(exemptRoleIds) => f.change({ exemptRoleIds })} placeholder="+ Aggiungi ruolo protetto" />
              </Field>
              <Field label="Espelli chi non si verifica entro" info="Se chi entra non si verifica entro questo tempo, il bot lo espelle (riceve un messaggio privato e può rientrare). Sicurezze: vale solo per chi entra DOPO che l'hai attivata, mai per chi ha già altri ruoli, mai per booster, protetti, staff o chi è entrato con l'ingresso libero. Durante l'ingresso libero è in pausa."
                hint={v.kickAfterMinutes > 0 ? "Vale solo per chi entra da adesso in poi." : "Nessuno viene espulso."}>
                <SimpleSelect value={v.kickAfterMinutes ?? 0} onChange={(x) => f.change({ kickAfterMinutes: Number(x) })} options={KICK} />
              </Field>
            </Section>

            <Section title="Log" icon={History} info="Il bot scrive in questo canale ogni verifica, espulsione, booster protetto e inizio/fine dell'ingresso libero.">
              <Field label="Canale dei log di verifica" info="Meglio un canale visibile solo allo staff. Se non ce l'hai, crealo dal menu.">
                <ChannelPick value={v.logChannelId} onChange={(logChannelId) => f.change({ logChannelId })} types={["text", "announcement"]} placeholder="Nessuno" suggestName="log-verifica" />
              </Field>
            </Section>
            <PublishBar f={f} v={v} chName={chName} publishing={publishing} onPublish={publish} prepare={prepare} />
          </div>
        );
      }}
    </FeatureGate>
  );
}

/** Barra fissa in basso: sempre visibile, con il tasto per pubblicare. */
function PublishBar({ f, v, chName, publishing, onPublish, prepare }) {
  const missing = [!v.roleId && "ruolo dei verificati", !v.channelId && "canale di verifica"].filter(Boolean);
  const status = missing.length ? <span className="text-[#FEE75C]">Manca: {missing.join(", ")}</span>
    : f.dirty ? <span className="text-[#FEE75C]">Hai modifiche non salvate</span>
    : v.messageId && v.enabled ? <span className="text-[#57d38c]">✓ Pubblicata e attiva{chName ? ` in #${chName}` : ""}</span>
    : <span className="text-[#B5BAC1]">Pronta: premi «Salva e pubblica»</span>;
  return (
    <div className="sticky bottom-4 z-20 flex flex-wrap items-center gap-3 rounded-2xl border border-[#5865F2]/40 bg-[#12141D]/95 px-4 py-3 shadow-[0_10px_40px_-10px_rgba(0,0,0,0.8)] backdrop-blur" data-testid="verify-publish-bar">
      <span className="min-w-[160px] flex-1 text-sm font-semibold">{status}</span>
      {f.dirty && <Button variant="ghost" onClick={f.reset} disabled={publishing || f.saving} className="text-[#B5BAC1] hover:bg-white/5 hover:text-white">Annulla</Button>}
      {f.dirty && <Button variant="outline" onClick={() => f.save(prepare)} disabled={publishing || f.saving} className={outlineBtn}>{f.saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Salva solo</Button>}
      <Button onClick={onPublish} disabled={publishing || f.saving} className="bg-[#23A55A] font-semibold text-white hover:bg-[#1e8e4d]" data-testid="verify-publish-main">
        {publishing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}{v.messageId ? "Salva e aggiorna su Discord" : "Salva e pubblica su Discord"}
      </Button>
    </div>
  );
}

// ─────────────────────────── elenchi ───────────────────────────
function Avatar({ src, name }) {
  return src ? <img src={src} alt="" className="h-8 w-8 rounded-full" /> : <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#232838] text-xs font-bold text-white">{(name || "?")[0]}</div>;
}

function PendingList({ ov, setOv }) {
  const now = useNow(15000);
  const [busy, setBusy] = useState(null);
  const act = async (id, fn, ok) => { setBusy(id); try { setOv(await fn(id)); toast.success(ok); } catch (e) { toast.error(apiError(e)); } finally { setBusy(null); } };
  if (!ov.pending.length) return <Empty icon={UserCheck} title="Nessuno in attesa">Tutti i membri del server sono verificati.</Empty>;
  return (
    <div className="overflow-hidden rounded-2xl border border-[#232838] bg-[#161923]">
      <div className="hidden grid-cols-[minmax(0,1fr)_130px_130px_170px_210px] gap-3 border-b border-[#232838] px-4 py-2.5 text-[11px] font-bold uppercase tracking-wide text-[#6b7185] md:grid">
        <span>Utente</span><span>Entrato</span><span>Account creato</span><span>Stato</span><span />
      </div>
      {ov.pending.map((m) => (
        <div key={m.id} className="grid gap-2 border-b border-[#232838] px-4 py-3 last:border-0 md:grid-cols-[minmax(0,1fr)_130px_130px_170px_210px] md:items-center md:gap-3">
          <div className="flex min-w-0 items-center gap-2.5"><Avatar src={m.avatar} name={m.username} /><div className="min-w-0"><p className="truncate text-sm font-semibold text-white">{m.displayName}</p><p className="truncate text-xs text-[#949BA4]">@{m.username}</p></div></div>
          <span className="text-sm text-[#B5BAC1]" title={formatDateTime(m.joinedAt)}>{m.joinedAt ? timeAgo(m.joinedAt) : "—"}</span>
          <span className="text-sm text-[#B5BAC1]" title={formatDateTime(m.createdAt)}>{timeAgo(m.createdAt)}</span>
          <span className="text-xs">
            {m.protected ? <span className="rounded-full bg-[#23A55A]/15 px-2 py-0.5 font-semibold text-[#57d38c]">🛡️ Protetto{m.protectedReason ? ` · ${m.protectedReason}` : ""}</span>
              : m.kickAt ? <span className="rounded-full bg-[#F23F43]/15 px-2 py-0.5 font-semibold text-[#ff9a9c]">Espulso tra {Math.max(0, Math.ceil((new Date(m.kickAt).getTime() - now) / 60000))} min</span>
              : <span className="rounded-full bg-[#FEE75C]/10 px-2 py-0.5 font-semibold text-[#FEE75C]">In attesa</span>}
          </span>
          <div className="flex gap-1.5 md:justify-end">
            <Button size="sm" disabled={busy === m.id} onClick={() => act(m.id, api.verify, "Utente verificato")} className={`h-8 ${primaryBtn}`}><BadgeCheck className="mr-1 h-3.5 w-3.5" />Verifica</Button>
            {!m.protected && <Button size="sm" variant="outline" disabled={busy === m.id} onClick={() => act(m.id, api.protect, "Utente protetto")} className={`h-8 ${outlineBtn}`}><ShieldPlus className="mr-1 h-3.5 w-3.5" />Proteggi</Button>}
          </div>
        </div>
      ))}
    </div>
  );
}

function ProtectedList({ ov, setOv }) {
  const [busy, setBusy] = useState(null);
  const remove = async (id) => { setBusy(id); try { setOv(await api.unprotect(id)); toast.success("Protezione tolta"); } catch (e) { toast.error(apiError(e)); } finally { setBusy(null); } };
  if (!ov.protected.length) return <Empty icon={ShieldPlus} title="Nessun utente protetto">Qui compaiono chi entra con l'ingresso libero, chi mette il boost e chi proteggi a mano.</Empty>;
  return (
    <div className="overflow-hidden rounded-2xl border border-[#232838] bg-[#161923]">
      {ov.protected.map((p) => (
        <div key={p.id} className="flex flex-wrap items-center gap-3 border-b border-[#232838] px-4 py-3 last:border-0">
          <div className="min-w-0 flex-1"><p className="text-sm font-semibold text-white">{p.username || p.id}</p><p className="text-xs text-[#949BA4]">{p.reason} · {formatDateTime(p.at)}{p.inServer ? "" : " · non è più nel server"}</p></div>
          <Button size="sm" variant="outline" disabled={busy === p.id} onClick={() => remove(p.id)} className={`h-8 ${outlineBtn}`}>Togli protezione</Button>
        </div>
      ))}
    </div>
  );
}

const EVENT = {
  verified: ["✅", "Verificato", "#57F287"], kicked: ["👢", "Espulso (non verificato)", "#ED4245"], booster: ["💎", "Booster protetto", "#F47FFF"],
  "free-join": ["🚪", "Entrato con ingresso libero", "#23A55A"], "free-start": ["🟢", "Ingresso libero attivato", "#23A55A"], "free-end": ["⏹️", "Ingresso libero terminato", "#80848E"],
  failed: ["❌", "Verifica fallita", "#FEE75C"], protected: ["🛡️", "Protetto a mano", "#5865F2"],
};
function EventLog({ ov }) {
  if (!ov.events.length) return <Empty icon={History} title="Registro vuoto">Qui compariranno verifiche, espulsioni, booster e ingressi liberi.</Empty>;
  return (
    <div className="overflow-hidden rounded-2xl border border-[#232838] bg-[#161923]">
      {ov.events.map((e, i) => {
        const [icon, label, tone] = EVENT[e.type] ?? ["•", e.type, "#80848E"];
        const extra = e.type === "free-start" ? `per ${e.minutes} min${e.autoVerify ? " · ruolo subito" : ""}` : e.type === "free-end" ? `${e.joined} entrati · ${e.reason}` : e.how;
        return (
          <div key={i} className="flex items-center gap-3 border-b border-[#232838] px-4 py-2.5 last:border-0">
            <span className="text-lg">{icon}</span>
            <div className="min-w-0 flex-1">
              <p className="text-sm text-white"><b style={{ color: tone }}>{label}</b>{e.username ? <> · {e.username}</> : null}</p>
              {extra && <p className="text-xs text-[#949BA4]">{extra}</p>}
            </div>
            <span className="shrink-0 text-xs text-[#6b7185]" title={formatDateTime(e.at)}>{timeAgo(e.at)}</span>
          </div>
        );
      })}
    </div>
  );
}

// ─────────────────────────── pagina ───────────────────────────
export default function VerificationPage() {
  const f = useFeature("verification");
  const [ov, setOv] = useState(null);
  const [tab, setTab] = useState("settings");
  const timer = useRef(null);
  const load = useCallback(() => api.overview().then(setOv).catch((e) => toast.error(apiError(e))), []);
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);
  useEffect(() => {
    const on = (ev) => {
      const e = ev.detail?.event || "";
      if (e.startsWith("verification.") || e.startsWith("member.") || e.startsWith("antiraid.")) { clearTimeout(timer.current); timer.current = setTimeout(load, 700); }
    };
    window.addEventListener("kalyx:live", on);
    return () => window.removeEventListener("kalyx:live", on);
  }, [load]);
  // Dopo un salvataggio aggiorna anche avvisi e riepilogo.
  const savedRef = useRef(f.value);
  useEffect(() => { if (!f.dirty && f.value !== savedRef.current) { savedRef.current = f.value; load(); } }, [f.value, f.dirty, load]);

  const s = ov?.stats ?? {};
  return (
    <div className="space-y-6">
      <PageHeader icon={ShieldCheck} title="Verifica all'ingresso" subtitle="Chi entra deve verificarsi per vedere il server. Con l'ingresso libero e la protezione dei booster nessuno che vuoi tu viene bloccato."
        actions={f.value && <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold ${f.value.enabled ? "bg-[#23A55A]/15 text-[#57d38c]" : "bg-white/5 text-[#949BA4]"}`}><span className={`h-2 w-2 rounded-full ${f.value.enabled ? "bg-[#23A55A]" : "bg-[#80848E]"}`} />{f.value.enabled ? "Attiva" : "Spenta"}</span>} />
      <HowItWorks id="verification"
        intro="Protegge il server dai bot: i nuovi arrivati vedono solo il canale di verifica finché non premono il pulsante."
        steps={[
          <>Crea un ruolo (es. <b className="text-white">Membro</b>) e fai in modo che i canali li veda solo quel ruolo, mentre <b className="text-white">#verifica</b> la vede @everyone.</>,
          <>Qui scegli quel ruolo, il canale e premi <b className="text-white">Salva</b>, poi <b className="text-white">Pubblica</b>.</>,
          <>Devi far entrare persone per i boost? Premi prima <b className="text-white">Attiva ingresso libero</b>: niente verifica, niente anti-raid, nessuna espulsione.</>,
        ]}
        tips={["Chi mette il boost viene verificato e protetto in automatico, per sempre.", "L'espulsione dei non verificati è spenta finché non la accendi tu, e non tocca mai chi era già nel server."]} />

      <FreeEntryCard ov={ov} setOv={setOv} />
      {ov?.raid?.active && (
        <div className="flex items-center gap-2 rounded-2xl border border-[#F23F43]/40 bg-[#F23F43]/10 p-4 text-sm text-[#ffb3b4]"><Siren className="h-4 w-4" />Modalità raid attiva: attivando l'ingresso libero viene fermata subito.</div>
      )}

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <Stat icon={BadgeCheck} value={s.verifiedToday} label="Verificati oggi" tone="#57F287" info="Quante persone hanno completato la verifica oggi." />
        <Stat icon={CheckCircle2} value={s.verified} label="Verificati in totale" tone="#5865F2" info="Tutte le verifiche completate da quando esiste questa sezione (pulsante, captcha, booster, a mano)." />
        <Stat icon={Clock} value={s.pending} label="In attesa" tone="#FEE75C" info="Membri del server che non hanno ancora il ruolo dei verificati." />
        <Stat icon={Gem} value={s.protected} label="Protetti" tone="#F47FFF" info="Persone che non verranno mai espulse: entrate con l'ingresso libero, booster o protette a mano." />
        <Stat icon={UserX} value={s.kicked} label="Espulsi" tone="#ED4245" info="Persone espulse perché non si sono verificate in tempo." />
      </div>
      <Health issues={ov?.health} />

      <PillTabs active={tab} onChange={setTab} tabs={[
        { id: "settings", label: "Impostazioni", icon: ShieldCheck },
        { id: "pending", label: `In attesa${s.pending ? ` (${s.pending})` : ""}`, icon: Users },
        { id: "protected", label: "Protetti", icon: ShieldPlus },
        { id: "log", label: "Registro", icon: History },
      ]} />
      {tab === "settings" && <Settings f={f} onPublished={load} />}
      {tab !== "settings" && !ov && <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-[#949BA4]" /></div>}
      {tab === "pending" && ov && <PendingList ov={ov} setOv={setOv} />}
      {tab === "protected" && ov && <ProtectedList ov={ov} setOv={setOv} />}
      {tab === "log" && ov && <EventLog ov={ov} />}
      <p className="flex items-center gap-1.5 text-xs text-[#6b7185]"><Info className="h-3.5 w-3.5" />Si aggiorna da solo quando succede qualcosa su Discord.</p>
    </div>
  );
}
